import { 
  Workflow, 
  WorkflowTemplate, 
  WorkflowExecution, 
  WorkflowStatsResponse,
  WorkflowListResponse,
  WorkflowExecutionResponse,
  WorkflowFormData,
  WorkflowAnalytics,
  WorkflowScope
} from '../types/workflow';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const API_BASE = `${API_BASE_URL}/api/workflows`;

class WorkflowService {
  // Workflow CRUD operations
  async getWorkflows(
    page = 1, 
    limit = 50, 
    search?: string, 
    scope: WorkflowScope = 'personal',
    starred?: boolean
  ): Promise<WorkflowListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      scope,
      ...(search && { search }),
      ...(starred !== undefined && { starred: starred.toString() })
    });

    const response = await fetch(`${API_BASE}?${params}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch workflows');
    }
    
    const result = await response.json();
    if (result.success && result.data) {
      return result.data;
    }
    return {
      workflows: [],
      pagination: { page: 1, limit: 50, total: 0, pages: 0 },
      scope,
      starred: starred || false
    };
  }

  async getWorkflow(id: string): Promise<Workflow> {
    const response = await fetch(`${API_BASE}/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch workflow');
    }
    const result = await response.json();
    return result.success ? result.data : result;
  }

  async createWorkflow(data: WorkflowFormData): Promise<Workflow> {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to create workflow');
    }
    const result = await response.json();
    return result.success ? result.data : result;
  }

  async updateWorkflow(id: string, data: Partial<WorkflowFormData>): Promise<Workflow> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update workflow');
    }
    const result = await response.json();
    return result.success ? result.data : result;
  }

  async deleteWorkflow(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to delete workflow');
    }
  }

  async duplicateWorkflow(id: string, name?: string): Promise<Workflow> {
    const response = await fetch(`${API_BASE}/${id}/duplicate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      throw new Error('Failed to duplicate workflow');
    }
    return response.json();
  }

  // Star/Unstar workflow
  async toggleStar(workflowId: string): Promise<{ starred: boolean; message: string }> {
    const response = await fetch(`${API_BASE}/${workflowId}/star`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to toggle workflow star');
    }
    
    const result = await response.json();
    if (result.success && result.data) {
      return result.data;
    }
    throw new Error('Failed to parse star response');
  }

  async getStarredWorkflows(page = 1, limit = 50): Promise<WorkflowListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    const response = await fetch(`${API_BASE}/starred?${params}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch starred workflows');
    }
    
    const result = await response.json();
    if (result.success && result.data) {
      return {
        ...result.data,
        scope: 'personal' as WorkflowScope,
        starred: true
      };
    }
    return {
      workflows: [],
      pagination: { page: 1, limit: 50, total: 0, pages: 0 },
      scope: 'personal',
      starred: true
    };
  }

  // Execution operations
  async executeWorkflow(id: string, params: {
    testMode?: boolean;
    debugMode?: boolean;
    campaignId?: string;
    contactEmail?: string;
    conversationId?: string;
  } = {}): Promise<WorkflowExecutionResponse> {
    const response = await fetch(`${API_BASE}/${id}/execute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error('Failed to execute workflow');
    }
    return response.json();
  }

  async getExecutions(workflowId?: string, page = 1, limit = 50): Promise<WorkflowExecution[]> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(workflowId && { workflowId })
    });

    const response = await fetch(`${API_BASE}/executions?${params}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch executions');
    }
    
    const data = await response.json();
    return data.executions || [];
  }

  async getExecution(id: string): Promise<WorkflowExecution> {
    const response = await fetch(`${API_BASE}/executions/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch execution');
    }
    return response.json();
  }

  async cancelExecution(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/executions/${id}/cancel`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error('Failed to cancel execution');
    }
  }

  // Template operations
  async getTemplates(category?: string): Promise<WorkflowTemplate[]> {
    const params = new URLSearchParams();
    if (category) {
      params.append('category', category);
    }

    const response = await fetch(`${API_BASE}/templates?${params}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch templates');
    }
    
    const data = await response.json();
    return data.templates || [];
  }

  async getTemplate(id: string): Promise<WorkflowTemplate> {
    const response = await fetch(`${API_BASE}/templates/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch template');
    }
    return response.json();
  }

  async createTemplate(workflowId: string, data: Partial<WorkflowTemplate>): Promise<WorkflowTemplate> {
    const response = await fetch(`${API_BASE}/${workflowId}/template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to create template');
    }
    return response.json();
  }

  async useTemplate(templateId: string, name?: string): Promise<Workflow> {
    const response = await fetch(`${API_BASE}/templates/${templateId}/use`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      throw new Error('Failed to use template');
    }
    return response.json();
  }

  // Analytics operations
  async getStats(scope: WorkflowScope = 'personal'): Promise<WorkflowStatsResponse> {
    const params = new URLSearchParams({ scope });
    const response = await fetch(`${API_BASE}/stats?${params}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch stats');
    }
    
    const result = await response.json();
    if (result.success && result.data) {
      return { ...result.data, scope };
    }
    
    return {
      totalWorkflows: 0,
      activeWorkflows: 0,
      totalExecutions: 0,
      successRate: 0,
      scope
    };
  }

  async getAnalytics(workflowId: string, period = '7d'): Promise<WorkflowAnalytics> {
    const response = await fetch(`${API_BASE}/${workflowId}/analytics?period=${period}`);
    if (!response.ok) {
      throw new Error('Failed to fetch analytics');
    }
    return response.json();
  }

  async getWorkflowMetrics(workflowId: string, startDate?: Date, endDate?: Date) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const response = await fetch(`${API_BASE}/${workflowId}/metrics?${params}`);
    if (!response.ok) {
      throw new Error('Failed to fetch workflow metrics');
    }
    return response.json();
  }

  // Testing operations
  async testWorkflow(id: string, testData?: Record<string, any>): Promise<WorkflowExecutionResponse> {
    const response = await fetch(`${API_BASE}/${id}/test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ testData }),
    });

    if (!response.ok) {
      throw new Error('Failed to test workflow');
    }
    return response.json();
  }

  async validateWorkflow(workflow: Workflow): Promise<{ isValid: boolean; errors: string[] }> {
    const response = await fetch(`${API_BASE}/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(workflow),
    });

    if (!response.ok) {
      throw new Error('Failed to validate workflow');
    }
    const result = await response.json();
    return result.data || { isValid: false, errors: ['Validation failed'] };
  }

  // Preview operations
  async previewWorkflow(workflow: Workflow): Promise<any> {
    const response = await fetch(`${API_BASE}/preview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(workflow),
    });

    if (!response.ok) {
      throw new Error('Failed to preview workflow');
    }
    return response.json();
  }

  // Import/Export operations
  async exportWorkflow(id: string): Promise<Blob> {
    const response = await fetch(`${API_BASE}/${id}/export`);
    if (!response.ok) {
      throw new Error('Failed to export workflow');
    }
    return response.blob();
  }

  async importWorkflow(file: File): Promise<Workflow> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/import`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to import workflow');
    }
    return response.json();
  }

  // Real-time operations
  subscribeToExecution(executionId: string, callback: (event: any) => void): () => void {
    const eventSource = new EventSource(`${API_BASE}/executions/${executionId}/events`);
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        callback(data);
      } catch (error) {
        console.error('Failed to parse execution event:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('Execution event stream error:', error);
    };

    return () => {
      eventSource.close();
    };
  }

  subscribeToWorkflowEvents(workflowId: string, callback: (event: any) => void): () => void {
    const eventSource = new EventSource(`${API_BASE}/${workflowId}/events`);
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        callback(data);
      } catch (error) {
        console.error('Failed to parse workflow event:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('Workflow event stream error:', error);
    };

    return () => {
      eventSource.close();
    };
  }

  // Utility methods
  async getAvailableActions(): Promise<any[]> {
    const response = await fetch(`${API_BASE}/actions`);
    if (!response.ok) {
      throw new Error('Failed to fetch available actions');
    }
    const data = await response.json();
    return data.actions || [];
  }

  async getAvailableTriggers(): Promise<any[]> {
    const response = await fetch(`${API_BASE}/triggers`);
    if (!response.ok) {
      throw new Error('Failed to fetch available triggers');
    }
    const data = await response.json();
    return data.triggers || [];
  }

  async getIntegrations(): Promise<any[]> {
    const response = await fetch(`${API_BASE}/integrations`);
    if (!response.ok) {
      throw new Error('Failed to fetch integrations');
    }
    const data = await response.json();
    return data.integrations || [];
  }
}

export const workflowService = new WorkflowService(); 